/* eslint-disable @typescript-eslint/indent */
import { Details, RequestDocuments, RequiredDocument } from '@/interfaces/requirement';
import { RequirementService } from '@/services/requirement/requirement.service';
import { StoreState } from '@/store';
import { modalShow } from '@/store/modal/actions';
import { normalize } from '@/utils/normalizeString';
import { dateEndMustBeAfterDateStart } from '@/validators/dateEndMustBeAfterDateStart';
import { dateEndSettedOrUndefined } from '@/validators/dateEndSettedOrUndefined';
import { dateLifeHistoryDocument30DaysOfVigency } from '@/validators/dateLifeHistoryDocument30DaysOfVigency';
import { dateMustBeBeforeOrEqualThanToday } from '@/validators/dateMustBeBeforeOrEqualThanToday';
import { fileMustBeUploaded } from '@/validators/fileMustBeUploaded';
import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { combineLatest, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';

type PreviousDocsExpiration = { id: number, willExpire: boolean, dateEmit?: Date, dateUntil?: Date, dateFrom?: Date };
interface RequiredDocumentWithExpirationFlag extends RequiredDocument {
  isAboutToExpire?: boolean;
  isDefaultActivated?: boolean;
}

@Component({
  selector: 'app-documents-load[driverIsEmployee]',
  templateUrl: './documents-load.component.html',
  styleUrls: ['./documents-load.component.css'],
})
export class DocumentsLoadComponent implements OnInit {
  @Input() numberSection: string = '';
  @Input() requirementId: number = 0;
  @Input() assetId: number = 0;
  @Input() licenseRequirementId?: number = 0;
  @Input() canReuseOldDocuments: boolean = false;
  @Input() driverMdmId: string = '';
  @Input() driverIsEmployee: boolean = false;
  @Input() requestType: 'NEW' | 'RENEW' | 'EXTEND' = 'NEW';

  @Input()
  set equipmentDataToSend(data: RequestDocuments[]) {
    this.isMineRequest = data.some((document) => document.conductionZone.includes('7'));
    this._equipmentDataToSend = data;
    this.getRequiredDocuments();
  }
  get equipmentDataToSend(): RequestDocuments[] {
    return this._equipmentDataToSend;
  }
  _equipmentDataToSend: RequestDocuments[] = [];
  @Input() documentFormArray: FormArray = new FormArray([]);

  requiredDocuments: RequiredDocumentWithExpirationFlag[] = [];

  gettingDocumentsRequired: boolean = true;

  previousDocsExpiration: PreviousDocsExpiration[] = [];
  waitUntilDetailsArrive$ = new Subject();
  getManyRequiredDocuments$ = new Subject<RequiredDocument[]>();

  detailData?: Details;

  isMineRequest: boolean = false;

  constructor(
    private requirementService: RequirementService,
    private store: Store<StoreState>,
  ) { }

  ngOnInit(): void {

    combineLatest(
      this.getManyRequiredDocuments$,
      this.waitUntilDetailsArrive$,
    ).subscribe({
      next: ([requiredDocuments]) => {
        let sortedRequDocs: RequiredDocumentWithExpirationFlag[] = requiredDocuments;
        if (this.previousDocsExpiration.length <= 1) {
          this.previousDocsExpiration = [];
        }

        if (this.canReuseOldDocuments && this.previousDocsExpiration.length > 1) {
          sortedRequDocs = requiredDocuments
            .map((doc) => {
              const docExpiration = this.previousDocsExpiration.find((exp) => doc.id === exp.id);
              const isAboutToExpire = docExpiration ? docExpiration.willExpire : true;
              return {
                ...doc,
                isAboutToExpire,
              };
            }).sort((a, b) => (b.isAboutToExpire ? 1 : 0) - (a.isAboutToExpire ? 1 : 0));
        }

        if (!this.canReuseOldDocuments) {
          this.generateFormArray(sortedRequDocs);
        }
        else if (this.previousDocsExpiration.length === 0) {
          this.generateFormArray(sortedRequDocs);
        }
        if (this.canReuseOldDocuments) {
          sortedRequDocs = sortedRequDocs.map((x) => {
            let isDefaultActivated = false;
            const document = this.previousDocsExpiration.find((y) => y.id === x.id);
            if (!document || (!document.dateEmit && !document.dateFrom && !document.dateUntil)) {
              this.generateFormControl(x);
              isDefaultActivated = true;
            }
            return { ...x, isDefaultActivated };
          }).sort((x) => (x.isDefaultActivated ? -1 : 1));
        }

        this.requiredDocuments = sortedRequDocs;

        this.gettingDocumentsRequired = false;
      },
      error: () => {

      },
    });

    if (this.licenseRequirementId && this.canReuseOldDocuments) {
      this.requirementService.getDetailsAll(this.licenseRequirementId).subscribe({
        next: (detailData) => {
          const { documents } = detailData;
          this.detailData = detailData;
          this.previousDocsExpiration = documents
            .reduce<PreviousDocsExpiration[]>((arr, doc) => {
              let diffDays = 999;
              if (doc.dateUntil) {
                const currentDate = new Date();
                currentDate.setHours(4, 4, 0, 0);
                doc.dateUntil.setHours(4, 4, 0, 0);

                const diffTime = Math.abs((<unknown>currentDate as number) - (<unknown>doc.dateUntil as number));
                diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
              }

              arr.push({
                id: doc.documentRequiredTypeId,
                willExpire: diffDays <= 45,
                dateEmit: doc.dateEmit,
                dateFrom: doc.dateFrom,
                dateUntil: doc.dateUntil,
              });
              return arr;
            }, []);

          this.waitUntilDetailsArrive$.next();
        },
      });
    }
    else {
      this.waitUntilDetailsArrive$.next();
    }
  }

  getRequiredDocuments() {
    console.log('a');
    this.gettingDocumentsRequired = true;
    this.requirementService.getManyRequiredDocuments(this.equipmentDataToSend)
      .pipe(tap((x) => { this.gettingDocumentsRequired = false; }))
      .subscribe({
        next: (requiredDocuments) => {
          this.getManyRequiredDocuments$.next(requiredDocuments);
        },
        error: () => {

        },
      });
  }

  getformGroupByRowIndex(index: number): FormGroup {
    return this.documentFormArray.at(index) as FormGroup;
  }

  getformGroupByItemId(id: number): FormGroup {
    const controlIndex = this.documentFormArray.value.findIndex((value: any) => value.id === id);
    return this.documentFormArray.at(controlIndex) as FormGroup;
  }

  formGroupExist(id: number) {
    return !!this.getformGroupByItemId(id);
  }

  generateFormArray(requiredDocuments: RequiredDocument[], defaultActivated: boolean = true) {
    this.documentFormArray.clear();
    requiredDocuments.map((x) => this.generateFormControl(x, defaultActivated));
  }

  generateFormControl(requiredDocument: RequiredDocument, defaultActivated: boolean = true) {
    const controlIndex = this.documentFormArray.value.findIndex((value: any) => value.id === requiredDocument.id);
    if (controlIndex >= 0) {
      this.documentFormArray.removeAt(controlIndex);
    }
    const controls: Record<string, FormControl> = {
      id: new FormControl(requiredDocument.id),
      file: new FormControl({
        status: 'initial',
        description: '',
        fileUrl: '',
        fileName: '',
      }, [fileMustBeUploaded()]),
      isActive: new FormControl(defaultActivated),
    };

    const validators = [];
    if (requiredDocument.emit) {
      const lifeHistoryDocumentNameRegExp = new RegExp(normalize('Hoja de vida'));

      if (lifeHistoryDocumentNameRegExp.test(normalize(requiredDocument.name))) {
        controls.dateEmit = new FormControl(null, [Validators.required, dateLifeHistoryDocument30DaysOfVigency(), dateMustBeBeforeOrEqualThanToday()]);
      }
      else {
        controls.dateEmit = new FormControl(null, [Validators.required]);
      }
    }
    else {
      controls.dateFrom = new FormControl(null, [Validators.required, dateMustBeBeforeOrEqualThanToday()]);
      controls.dateUntil = new FormControl(null, [dateEndSettedOrUndefined()]);
      validators.push(dateEndMustBeAfterDateStart('dateFrom', 'dateUntil'));
    }

    const formGroup = new FormGroup(controls, validators);
    this.documentFormArray.push(formGroup);
  }

  asFormControl(abstractControl: AbstractControl): FormControl {
    return abstractControl as FormControl;
  }

  documentFileErrorByItemId(id: number): string {
    const formGroupControl = this.getformGroupByItemId(id);

    const error = formGroupControl.controls.file.errors;
    if (error) {
      if (error.description) {
        return error.description;
      }
    }
    return '';
  }

  documentDateFromErrorByItemId(id: number): string {
    const formGroupControl = this.getformGroupByItemId(id);

    const error = formGroupControl.controls.dateFrom.errors;
    if (error) {
      if (error.description) {
        return error.description;
      }
    }
    return '';
  }

  documentDateRangeErrorByItemId(id: number): string {
    const formGroupControl = this.getformGroupByItemId(id);

    const error = formGroupControl.errors;
    if (error) {
      if (error.description) {
        return error.description;
      }
    }
    return '';
  }

  documentDateEmitErrorByItemId(id: number): string {
    const formGroupControl = this.getformGroupByItemId(id);

    const error = formGroupControl.controls.dateEmit.errors;
    if (error) {
      if (error.description) {
        return error.description;
      }
    }
    return '';
  }

  showModalDocumentsRequired() {
    let outMessage = '';
    let counter = 0;
    this.requiredDocuments.map((document) => {
      const formGroup = this.getformGroupByItemId(document.id);
      if (formGroup) {
        if (formGroup.invalid) {
          counter++;
          outMessage += `${counter}.- ${document.name}:`;
          if (formGroup.controls.file.invalid) {
            outMessage += '\n* Documento. ';
          }
          if (formGroup.controls.dateEmit && formGroup.controls.dateEmit.invalid) {
            outMessage += '\n* Fecha de Emision';
          }
          if (formGroup.controls.dateFrom && formGroup.controls.dateFrom.invalid) {
            outMessage += '\n* Fecha desde ';
          }
          if (formGroup.controls.dateUntil && formGroup.controls.dateUntil.invalid) {
            outMessage += '\n* Fecha hasta';
          }
          outMessage += '\n\n';
        }
      }
    });
    if (outMessage) {
      this.store.dispatch(
        modalShow({
          title: 'Debe completar todos los documentos.',
          text: `Los siguientes campos aun no han sido completados: \n${outMessage}`,
          acceptButtonText: 'Aceptar',
          actionOnAccept: () => { },
        }),
      );
    }

  }

  getPrevDocsById(id: number): PreviousDocsExpiration {
    const finded = this.previousDocsExpiration.find((doc) => doc.id === id)
    if (!finded) {
      const requiredDocument = this.requiredDocuments.find((doc) => doc.id === id)!;
      return {
        id: requiredDocument.id,
        willExpire: true,
        // dateEmit: requiredDocument.emit,
        // dateFrom: requiredDocument.from,
        // dateUntil: requiredDocument.until
      };
    }
    return finded;
  }

  activateFormById(id: number) {
    const requiredDocument = this.requiredDocuments.find((doc) => doc.id === id);
    if (requiredDocument) {
      this.generateFormControl(requiredDocument, true);
    }
  }

  getTextOfEntity(text?: string): string {
    if (!text) {
      return '';
    }
    if (!(text.includes('Empleados:') || text.includes('Contratistas:'))) {
      return text;
    }
    const searchKey = this.driverIsEmployee ? 'Empleados:' : 'Contratistas:';

    if (!text.includes(searchKey)) {
      return '';
    }
    const textList = text.split('\\n');

    return textList.find((x) => x.includes(searchKey))!.replace(searchKey, '').trim();
  }

}
